import { atom } from "jotai";
import { setDashTrialNotificationsLastSeen } from "..";
import { DEFAULT_LAST_SEEN, DashTrialNotificationsLastSeen } from "../service";

/**
 * The atoms in this file should typically not be exposed directly. Instead, use the re-exported
 * versions from the `useNotificationsLastSeen` hook. Using the hook ensures that the atoms are
 * initialized correctly from the backend. The `notificationsLastSeenAtom` here originally did
 * an async read from the backend to initialize its value, but unfortunately it was found to
 * break dash standalone, and so this clunky hook/re-export solution was created.
 */

/**
 * Use `updateNotificationsLastSeenAtom` for setting values instead of directly setting on
 * this atom. Otherwise the backend service will not be updated.
 */
export const notificationsLastSeenAtom =
  atom<DashTrialNotificationsLastSeen>(DEFAULT_LAST_SEEN);

// Derived read-only atoms for individual notification types
export const lastSeenChatNotificationAtom = atom(
  (get) => get(notificationsLastSeenAtom).chat,
);

export const lastSeenConnectorNotificationAtom = atom(
  (get) => get(notificationsLastSeenAtom).connector,
);

export const lastSeenOmniNotificationAtom = atom(
  (get) => get(notificationsLastSeenAtom).omni,
);

export const lastSeenSerpNotificationAtom = atom(
  (get) => get(notificationsLastSeenAtom).serp,
);

export const lastSeenStacksNotificationAtom = atom(
  (get) => get(notificationsLastSeenAtom).stacks,
);

export const lastSeenTrialEndingModalAtom = atom(
  (get) => get(notificationsLastSeenAtom).modal,
);

/**
 * This atom can set one or more values at once, and updates the values in UserMetadata.
 */
export const updateNotificationsLastSeenAtom = atom(
  null,
  (get, set, updates: Partial<DashTrialNotificationsLastSeen>) => {
    const current = get(notificationsLastSeenAtom);
    const newValue = { ...current, ...updates };
    set(notificationsLastSeenAtom, newValue);

    // Persist to backend service
    try {
      setDashTrialNotificationsLastSeen(newValue);
    } catch (e) {
      // swallow error here
    }
  },
);
